import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tag } from 'primereact/tag';
import { Chart } from 'primereact/chart';
import axios from 'axios';
import { url } from '../../../../../env';
import OperationalReviewModal from '../shared/OperationalReviewModal';
import {
    AppTextField, AppNumberField, AppButton, AppIconButton,
} from '../../../../../Components/FormKit';

// ─── Constantes ──────────────────────────────────────────────────────────────
const TEAL = '#00897B';
const TEAL_LIGHT = '#E0F2F1';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const isDocCompleted = (status) => status === 'completed' || status === 'ok' || status === 'warning';

const isValidFinancialDoc = (doc) => {
    if (doc.status === 'error' || doc.status === 'failed') return false;
    if (doc.extracted?.es_documento_valido === false) return false;
    return true;
};

const fmtCurrency = (v) => {
    if (v === null || v === undefined) return 'N/A';
    return new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: 'MXN',
        minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(v);
};

const fmtPct = (v) => {
    if (v === null || v === undefined) return 'N/A';
    const pct = typeof v === 'number' && Math.abs(v) <= 1 ? v * 100 : v;
    return `${Number(pct).toFixed(1)}%`;
};

const NA = () => <span className="text-500 font-italic">N/A</span>;

const dotGet = (obj, path) => path.split('.').reduce((o, k) => (o && typeof o === 'object' ? o[k] : undefined), obj);

const FIN_EDIT_SECTIONS = [
    {
        title: 'Identificación',
        fields: [
            { path: 'empresa',  label: 'Empresa',  type: 'text' },
            { path: 'periodo',  label: 'Periodo',  type: 'text' },
        ],
    },
    {
        title: 'Balance General',
        fields: [
            { path: 'balance_general.total_activo',               label: 'Activo Total',                   type: 'number', currency: true },
            { path: 'balance_general.total_pasivo',               label: 'Pasivo Total',                   type: 'number', currency: true },
            { path: 'balance_general.capital_contable_reportado', label: 'Capital Contable (reportado)',    type: 'number', currency: true },
            { path: 'balance_general.activo_circulante',          label: 'Activo Circulante',              type: 'number', currency: true },
            { path: 'balance_general.pasivo_circulante',          label: 'Pasivo Circulante',              type: 'number', currency: true },
        ],
    },
    {
        title: 'Estado de Resultados',
        fields: [
            { path: 'estado_resultados.ventas_totales',                 label: 'Ventas Totales',             type: 'number', currency: true },
            { path: 'estado_resultados.utilidad_neta',                  label: 'Utilidad Neta',              type: 'number', currency: true },
            { path: 'estado_resultados.utilidad_operacion',             label: 'Utilidad de Operación',      type: 'number', currency: true },
            { path: 'estado_resultados.utilidad_antes_impuestos',       label: 'Utilidad Antes Impuestos',   type: 'number', currency: true },
            { path: 'estado_resultados.depreciacion',                   label: 'Depreciación',               type: 'number', currency: true },
            { path: 'estado_resultados.amortizacion',                   label: 'Amortización',               type: 'number', currency: true },
            { path: 'estado_resultados.depreciacion_amortizacion_total',label: 'Dep. + Amort. Total',        type: 'number', currency: true },
            { path: 'estado_resultados.gastos_financieros',             label: 'Gastos Financieros',         type: 'number', currency: true },
        ],
    },
];

const LabelValue = ({ label, value, note }) => {
    const isNA = value === null || value === undefined || value === '' || value === 'N/A';
    return (
        <div className="mb-2">
            <div className="text-xs text-500 font-semibold mb-1" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label}
            </div>
            <div className="text-sm text-900 font-medium">
                {!isNA ? String(value) : <NA />}
            </div>
            {note && (
                <div className="text-xs text-500 mt-1" style={{ lineHeight: 1.4 }}>
                    {note}
                </div>
            )}
        </div>
    );
};

const TextBlock = ({ label, value }) => (
    <div className="mb-2">
        <div className="text-xs text-500 font-semibold mb-1" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {label}
        </div>
        {value ? (
            <div className="text-sm text-700 border-round p-2" style={{ lineHeight: 1.6, background: TEAL_LIGHT, borderLeft: `3px solid ${TEAL}` }}>
                {value}
            </div>
        ) : <NA />}
    </div>
);

// ─── Skeleton ────────────────────────────────────────────────────────────────
const Skel = ({ h = '1rem', w = '100%' }) => (
    <div style={{ height: h, width: w, background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', borderRadius: '4px', animation: 'finSkel 1.4s infinite' }} />
);

const FinancialSkeleton = () => (
    <div className="flex flex-column gap-3">
        <style>{`@keyframes finSkel{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        <div className="flex gap-3 flex-wrap">
            {[1, 2, 3].map(i => (
                <div key={i} className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ flex: '1 1 240px' }}>
                    <Skel h="1rem" w="50%" /><div className="mb-2" />
                    <Skel h="0.85rem" /><div className="mb-1" />
                    <Skel h="0.85rem" w="70%" />
                </div>
            ))}
        </div>
        {[1, 2].map(i => (
            <div key={i} className="bg-white border-1 surface-border border-round-lg p-3">
                <Skel h="1rem" w="50%" /><div className="mb-2" />
                <Skel h="0.85rem" /><div className="mb-1" />
                <Skel h="0.85rem" w="80%" />
            </div>
        ))}
    </div>
);

// ─── Gráfica: Composición Patrimonial (doughnut) ────────────────────────────
const ComposicionPatrimonialChart = ({ balance }) => {
    if (!balance || (balance.total_pasivo == null && balance.capital_contable == null)) {
        return (
            <div className="flex align-items-center justify-content-center text-xs text-500 font-italic" style={{ height: '220px' }}>
                Sin datos de balance general
            </div>
        );
    }
    const data = {
        labels: ['Pasivo Total', 'Capital Contable'],
        datasets: [{
            data: [balance.total_pasivo || 0, balance.capital_contable || 0],
            backgroundColor: ['#EF5350', '#66BB6A'],
            borderWidth: 0,
        }]
    };
    const options = {
        cutout: '60%',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { font: { size: 11 } } },
            tooltip: {
                callbacks: { label: (ctx) => `${ctx.label}: ${fmtCurrency(ctx.raw)}` }
            }
        }
    };
    return (
        <div style={{ height: '220px' }}>
            <Chart type="doughnut" data={data} options={options} style={{ height: '100%' }} />
        </div>
    );
};

// ─── Gráfica: Estado de Resultados (bar) ────────────────────────────────────
const ResultadosChart = ({ resultados }) => {
    if (!resultados || (resultados.ventas_totales == null && resultados.utilidad_neta == null)) {
        return (
            <div className="flex align-items-center justify-content-center text-xs text-500 font-italic" style={{ height: '220px' }}>
                Sin datos de estado de resultados
            </div>
        );
    }
    const data = {
        labels: ['Ventas', 'EBITDA', 'Utilidad Neta', 'Gastos Fin.'],
        datasets: [{
            label: 'MXN',
            data: [
                resultados.ventas_totales || 0,
                resultados.ebitda || 0,
                resultados.utilidad_neta || 0,
                resultados.gastos_financieros || 0,
            ],
            backgroundColor: ['#42A5F5', '#FFA726', '#66BB6A', '#EF5350'],
            borderRadius: 4,
        }]
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => fmtCurrency(ctx.raw) } }
        },
        scales: {
            y: { ticks: { callback: (v) => fmtCurrency(v), font: { size: 10 } } }
        }
    };
    return (
        <div style={{ height: '220px' }}>
            <Chart type="bar" data={data} options={options} style={{ height: '100%' }} />
        </div>
    );
};

// ─── Gráfica: Comparativa Solvencia (grouped bar across periods) ────────────
const SolvenciaComparativaChart = ({ docs }) => {
    const labels = docs.map(d => d.periodo || d.empresa || 'Sin periodo');
    const data = {
        labels,
        datasets: [
            {
                label: 'Activo Total',
                data: docs.map(d => d.balance_general?.total_activo || 0),
                backgroundColor: '#42A5F5',
                borderRadius: 4,
            },
            {
                label: 'Pasivo Total',
                data: docs.map(d => d.balance_general?.total_pasivo || 0),
                backgroundColor: '#EF5350',
                borderRadius: 4,
            },
            {
                label: 'Capital Contable',
                data: docs.map(d => d.balance_general?.capital_contable || 0),
                backgroundColor: '#66BB6A',
                borderRadius: 4,
            },
        ],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { font: { size: 11 } } },
            tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmtCurrency(ctx.raw)}` } },
        },
        scales: {
            y: { ticks: { callback: (v) => fmtCurrency(v), font: { size: 10 } } },
        },
    };
    return (
        <div style={{ height: '280px' }}>
            <Chart type="bar" data={data} options={options} style={{ height: '100%' }} />
        </div>
    );
};

// ─── Gráfica: Comparativa Razón Circulante (bar across periods) ─────────────
const RazonCirculanteComparativaChart = ({ docs }) => {
    const labels = docs.map(d => d.periodo || d.empresa || 'Sin periodo');
    const values = docs.map(d => {
        if (d.ratios?.razon_circulante != null) return d.ratios.razon_circulante;
        const ac = d.balance_general?.activo_circulante;
        const pc = d.balance_general?.pasivo_circulante;
        return ac != null && pc > 0 ? ac / pc : 0;
    });
    const data = {
        labels,
        datasets: [{
            label: 'Razón Circulante',
            data: values,
            backgroundColor: values.map(v => v >= 1 ? '#66BB6A' : '#EF5350'),
            borderRadius: 4,
        }],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => `${ctx.raw.toFixed(2)}x` } },
        },
        scales: {
            y: {
                ticks: { callback: (v) => `${v}x`, font: { size: 10 } },
                suggestedMin: 0,
            },
        },
    };
    return (
        <div style={{ height: '280px' }}>
            <Chart type="bar" data={data} options={options} style={{ height: '100%' }} />
        </div>
    );
};

// ─── Gráfica: Comparativa Rentabilidad (Margen Neto + EBITDA %) ─────────────
const RentabilidadComparativaChart = ({ docs }) => {
    const labels = docs.map(d => d.periodo || d.empresa || 'Sin periodo');
    const margenNetoData = docs.map(d => {
        const mn = d.ratios?.margen_neto;
        if (mn == null) return 0;
        return Math.abs(mn) <= 1 ? mn * 100 : mn;
    });
    const ebitdaPctData = docs.map(d => {
        const ebitda = d.estado_resultados?.ebitda;
        const ventas = d.estado_resultados?.ventas_totales;
        if (ebitda == null || !ventas) return 0;
        return (ebitda / ventas) * 100;
    });
    const data = {
        labels,
        datasets: [
            {
                label: 'Margen Neto %',
                data: margenNetoData,
                backgroundColor: '#66BB6A',
                borderRadius: 4,
            },
            {
                label: 'EBITDA %',
                data: ebitdaPctData,
                backgroundColor: '#FFA726',
                borderRadius: 4,
            },
        ],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { font: { size: 11 } } },
            tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toFixed(1)}%` } },
        },
        scales: {
            y: { ticks: { callback: (v) => `${v}%`, font: { size: 10 } } },
        },
    };
    return (
        <div style={{ height: '280px' }}>
            <Chart type="bar" data={data} options={options} style={{ height: '100%' }} />
        </div>
    );
};

// ─── Vista Comparativa ──────────────────────────────────────────────────────
const ComparativaView = ({ docs }) => {
    return (
        <div className="flex flex-column gap-3">
            {/* Solvencia */}
            <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ borderTop: '3px solid #1565C0' }}>
                <div className="flex align-items-center gap-2 mb-3">
                    <i className="pi pi-shield text-lg" style={{ color: '#1565C0' }} />
                    <span className="text-sm font-bold text-900" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Solvencia — Comparativa
                    </span>
                </div>
                <SolvenciaComparativaChart docs={docs} />
            </div>

            {/* Razón Circulante */}
            <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ borderTop: '3px solid #1565C0' }}>
                <div className="flex align-items-center gap-2 mb-3">
                    <i className="pi pi-shield text-lg" style={{ color: '#1565C0' }} />
                    <span className="text-sm font-bold text-900" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Razon Circulante — Comparativa
                    </span>
                </div>
                <RazonCirculanteComparativaChart docs={docs} />
            </div>

            {/* Rentabilidad */}
            <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ borderTop: '3px solid #2E7D32' }}>
                <div className="flex align-items-center gap-2 mb-3">
                    <i className="pi pi-chart-line text-lg" style={{ color: '#2E7D32' }} />
                    <span className="text-sm font-bold text-900" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Rentabilidad — Margen Neto y EBITDA %
                    </span>
                </div>
                <RentabilidadComparativaChart docs={docs} />
            </div>
        </div>
    );
};

// ─── Gráfica: Indicadores (horizontal bar) ──────────────────────────────────
const IndicadoresChart = ({ ratios, workingCapitalRatio }) => {
    if (!ratios && workingCapitalRatio == null) {
        return (
            <div className="flex align-items-center justify-content-center text-xs text-500 font-italic" style={{ height: '220px' }}>
                Sin indicadores disponibles
            </div>
        );
    }
    const apalVal = ratios?.apalancamiento != null
        ? (Math.abs(ratios.apalancamiento) <= 1 ? ratios.apalancamiento * 100 : ratios.apalancamiento)
        : 0;
    const margenVal = ratios?.margen_neto != null
        ? (Math.abs(ratios.margen_neto) <= 1 ? ratios.margen_neto * 100 : ratios.margen_neto)
        : 0;
    const wcVal = workingCapitalRatio != null ? workingCapitalRatio * 100 : 0;

    const data = {
        labels: ['Apalancamiento', 'Margen Neto', 'Razón Circulante'],
        datasets: [{
            label: '%',
            data: [apalVal, margenVal, wcVal],
            backgroundColor: ['#EF5350', '#66BB6A', '#42A5F5'],
            borderRadius: 4,
        }]
    };
    const options = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => `${ctx.raw.toFixed(1)}%` } }
        },
        scales: {
            x: { ticks: { callback: (v) => `${v}%`, font: { size: 10 } } }
        }
    };
    return (
        <div style={{ height: '220px' }}>
            <Chart type="bar" data={data} options={options} style={{ height: '100%' }} />
        </div>
    );
};

// ─── Summary Card ────────────────────────────────────────────────────────────
const SummaryCard = ({ icon, title, items, borderColor }) => (
    <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1"
         style={{ flex: '1 1 280px', minWidth: '240px', borderTop: `3px solid ${borderColor}` }}>
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-lg`} style={{ color: borderColor }} />
            <span className="text-sm font-bold text-900" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {title}
            </span>
        </div>
        <div className="flex flex-column gap-2">
            {items.map((item, i) => (
                <div key={i} className="flex flex-column">
                    <div className="flex justify-content-between align-items-center">
                        <span className="text-xs text-500" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {item.label}
                        </span>
                        <span className="text-sm font-semibold text-900">
                            {item.value === 'N/A' || item.value === '' || item.value == null ? <NA /> : item.value}
                        </span>
                    </div>
                    {item.note && (
                        <div className="text-xs text-400 mt-1" style={{ lineHeight: 1.3 }}>
                            {item.note}
                        </div>
                    )}
                </div>
            ))}
        </div>
    </div>
);

// ─── Narrative Card (para bloques largos de texto) ──────────────────────────
const NarrativeCard = ({ icon, title, texts, borderColor }) => (
    <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1"
         style={{ flex: '1 1 280px', minWidth: '240px', borderTop: `3px solid ${borderColor}` }}>
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-lg`} style={{ color: borderColor }} />
            <span className="text-sm font-bold text-900" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {title}
            </span>
        </div>
        <div className="flex flex-column gap-2">
            {texts.map((t, i) => (
                <div key={i}>
                    {t.label && (
                        <div className="text-xs text-500 font-semibold mb-1" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {t.label}
                        </div>
                    )}
                    <div className="text-sm text-700" style={{ lineHeight: 1.5 }}>
                        {t.value || <NA />}
                    </div>
                </div>
            ))}
        </div>
    </div>
);

// ─── DocCard ─────────────────────────────────────────────────────────────────
const FinDocCard = ({ doc, isLatest }) => {
    const [open, setOpen] = useState(true);
    const isAnalyzing = doc.status === 'processing' || doc.status === 'pending';
    const isFailed = doc.status === 'failed' || doc.status === 'error';
    const balance = doc.balance_general;
    const resultados = doc.estado_resultados;
    const ratios = doc.ratios;

    return (
        <div className="bg-white border-round-lg overflow-hidden shadow-1"
             style={{ border: '1px solid var(--surface-border)', borderLeft: `4px solid ${TEAL}` }}>
            {/* Header */}
            <div
                className="flex align-items-center gap-3 cursor-pointer"
                style={{ padding: '0.75rem 1rem', background: `${TEAL}08`, borderBottom: open ? '1px solid #f3f4f6' : 'none' }}
                onClick={() => setOpen(o => !o)}
            >
                <i className="pi pi-file-edit" style={{ fontSize: '1.1rem', color: TEAL, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="text-sm font-semibold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {doc.file_name || 'Estado Financiero'}
                    </div>
                    <div className="text-xs text-500 mt-1">
                        {doc.empresa && <span>{doc.empresa}</span>}
                        {doc.periodo && <span className="ml-2">| {doc.periodo}</span>}
                    </div>
                </div>
                <div className="flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                    {isAnalyzing && <Tag value="Analizando" severity="warning" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />}
                    {isFailed && <Tag value="Error" severity="danger" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />}
                    {isLatest && !isAnalyzing && !isFailed && (
                        <Tag value="Vigente" severity="success" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />
                    )}
                    {doc.review_status === 'approved' && (
                        <Tag value="Aprobado" severity="success" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />
                    )}
                    {doc.has_manual_overrides && (
                        <Tag value="Corregido" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', background: `${TEAL}20`, color: TEAL }} />
                    )}
                    {(doc.operational_completeness?.missing_required_paths?.length > 0) && (
                        <Tag value={`Faltan ${doc.operational_completeness.missing_required_paths.length}`} severity="warning" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />
                    )}
                    <i className={`pi ${open ? 'pi-chevron-up' : 'pi-chevron-down'} text-xs text-400`} />
                </div>
            </div>

            {/* Body */}
            {open && (
                <div className="p-3">
                    {isAnalyzing ? (
                        <div className="flex align-items-center gap-3 p-3 border-round" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                            <i className="pi pi-spin pi-spinner" style={{ color: '#D97706' }} />
                            <span className="text-sm" style={{ color: '#92400E' }}>Documento en proceso de analisis. Los datos apareceran al completarse.</span>
                        </div>
                    ) : isFailed ? (
                        <div className="flex align-items-center gap-3 p-3 border-round" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
                            <i className="pi pi-exclamation-triangle" style={{ color: '#DC2626' }} />
                            <span className="text-sm" style={{ color: '#991B1B' }}>El analisis de este documento fallo. Puede reintentarse desde la pestana Documentos.</span>
                        </div>
                    ) : !doc.extracted ? (
                        <div className="text-sm text-500 font-italic">Sin datos extraidos.</div>
                    ) : (
                        <div className="flex flex-column gap-3">
                            {/* Balance General */}
                            {balance && (
                                <div>
                                    <div className="text-xs text-500 font-semibold mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Balance General
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.5rem 1.5rem' }}>
                                        <LabelValue label="Activo Total" value={fmtCurrency(balance.total_activo)} />
                                        <LabelValue label="Pasivo Total" value={fmtCurrency(balance.total_pasivo)} />
                                        <LabelValue label="Capital Contable" value={fmtCurrency(balance.capital_contable)} note={balance.capital_contable_note} />
                                        <LabelValue label="Activo Circulante" value={fmtCurrency(balance.activo_circulante)} />
                                        <LabelValue label="Pasivo Circulante" value={fmtCurrency(balance.pasivo_circulante)} />
                                    </div>
                                </div>
                            )}

                            {/* Estado de Resultados */}
                            {resultados && (
                                <div>
                                    <div className="text-xs text-500 font-semibold mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Estado de Resultados
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.5rem 1.5rem' }}>
                                        <LabelValue label="Ventas Totales" value={fmtCurrency(resultados.ventas_totales)} />
                                        <LabelValue label="EBITDA" value={fmtCurrency(resultados.ebitda)} note={resultados.ebitda_note} />
                                        <LabelValue label="Utilidad Neta" value={fmtCurrency(resultados.utilidad_neta)} />
                                        <LabelValue label="Gastos Financieros" value={fmtCurrency(resultados.gastos_financieros)} />
                                    </div>
                                </div>
                            )}

                            {/* Ratios */}
                            {(ratios || balance) && (
                                <div>
                                    <div className="text-xs text-500 font-semibold mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Razones Financieras
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.5rem 1.5rem' }}>
                                        <LabelValue label="Apalancamiento" value={fmtPct(ratios?.apalancamiento)} note={ratios?.apalancamiento_note} />
                                        <LabelValue label="Margen Neto" value={fmtPct(ratios?.margen_neto)} note={ratios?.margen_neto_note} />
                                        <LabelValue 
                                            label="Razon Circulante" 
                                            value={ratios?.razon_circulante != null ? ratios.razon_circulante.toFixed(2) : (
                                                balance?.activo_circulante != null && balance?.pasivo_circulante > 0 
                                                    ? (balance.activo_circulante / balance.pasivo_circulante).toFixed(2) 
                                                    : null
                                            )} 
                                            note={ratios?.razon_circulante_note} 
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Narrativas */}
                            {doc.analisis_patrimonial && <TextBlock label="Analisis Patrimonial" value={doc.analisis_patrimonial} />}
                            {doc.impacto_mensualidad && <TextBlock label="Impacto en Mensualidad" value={doc.impacto_mensualidad} />}
                            {doc.identificacion_ingresos && <TextBlock label="Identificacion de Ingresos" value={doc.identificacion_ingresos} />}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Missing-path helpers ─────────────────────────────────────────────────────
const EBITDA_COMPOSITE_PATH = 'estado_resultados.utilidad_operacion+depreciacion_amortizacion (EBITDA incalculable)';
const EBITDA_FIELD_PATHS = [
    'estado_resultados.utilidad_operacion',
    'estado_resultados.depreciacion',
    'estado_resultados.amortizacion',
    'estado_resultados.depreciacion_amortizacion_total',
    'estado_resultados.utilidad_antes_impuestos',
    'estado_resultados.gastos_financieros',
];

const FIN_PATH_LABELS = {};
FIN_EDIT_SECTIONS.forEach(s => s.fields.forEach(f => { FIN_PATH_LABELS[f.path] = f.label; }));

const labelForFinPath = (path) => {
    if (path === EBITDA_COMPOSITE_PATH) return 'EBITDA — datos insuficientes para calcular';
    return FIN_PATH_LABELS[path] || path;
};

const isFinFieldMissing = (path, missingRequiredPaths) =>
    missingRequiredPaths.includes(path) ||
    (missingRequiredPaths.includes(EBITDA_COMPOSITE_PATH) && EBITDA_FIELD_PATHS.includes(path));

// ─── OperationalEditForm ──────────────────────────────────────────────────────
const OperationalEditForm = ({ doc, activeOverridePaths, editValues, saveReason, onFieldChange, onReasonChange, onClearPath, onSave, onCancel, saving, missingRequiredPaths = [] }) => {
    const [confirmRevert, setConfirmRevert] = useState(null);
    const rawAi = doc.raw_extracted || doc.extracted || {};
    return (
        <div style={{ position: 'relative' }} className="fk-root">

            {FIN_EDIT_SECTIONS.map(section => (
                <div key={section.title} className="mb-4">
                    <div className="text-xs font-semibold text-500 mb-2"
                         style={{ textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.35rem' }}>
                        {section.title}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
                        {section.fields.map(({ path, label, type, currency: isCurrency }) => {
                            const isConfirmed = activeOverridePaths.includes(path);
                            const isMissingPath = isFinFieldMissing(path, missingRequiredPaths);
                            const hasValue = editValues[path] !== '' && editValues[path] !== undefined && editValues[path] !== null;
                            const isMissing = isMissingPath && !hasValue;
                            const customVars = isConfirmed
                                ? { '--fk-border': TEAL, '--fk-bg': `${TEAL}08` }
                                : isMissing
                                    ? { '--fk-border': '#ef4444', '--fk-bg': '#fff5f5' }
                                    : undefined;
                            const trailing = (
                                <>
                                    {isMissing && !isConfirmed && (
                                        <span className="text-xs font-semibold ml-1"
                                              style={{ color: '#dc2626', background: '#fee2e2', padding: '0 0.35rem', borderRadius: '4px' }}>
                                            Requerido
                                        </span>
                                    )}
                                    {isConfirmed && (
                                        <span className="text-xs font-semibold ml-1"
                                              style={{ color: TEAL, background: `${TEAL}18`, padding: '0 0.35rem', borderRadius: '4px' }}>
                                            ✓ Confirmado
                                        </span>
                                    )}
                                    {isConfirmed && (
                                        <AppIconButton
                                            icon="pi pi-undo"
                                            onClick={() => setConfirmRevert({ path, label, aiValue: dotGet(rawAi, path) })}
                                            title="Revertir al valor de IA"
                                            size="0.7rem"
                                        />
                                    )}
                                </>
                            );
                            if (type === 'number') {
                                return (
                                    <div key={path} style={customVars}>
                                        <AppNumberField
                                            label={label}
                                            value={editValues[path] != null ? Number(editValues[path]) : null}
                                            onValueChange={e => onFieldChange(path, e.value)}
                                            placeholder={isMissing && !isConfirmed ? '← Requerido' : 'N/A'}
                                            invalid={isMissing}
                                            trailing={trailing}
                                            mode={isCurrency ? 'currency' : undefined}
                                            currency={isCurrency ? 'MXN' : undefined}
                                            currencyDisplay={isCurrency ? 'narrowSymbol' : undefined}
                                            useGrouping={isCurrency}
                                            minFractionDigits={isCurrency ? 2 : undefined}
                                        />
                                    </div>
                                );
                            }
                            return (
                                <div key={path} style={customVars}>
                                    <AppTextField
                                        label={label}
                                        value={editValues[path] ?? ''}
                                        onChange={e => onFieldChange(path, e.target.value)}
                                        placeholder={isMissing && !isConfirmed ? '← Requerido' : 'N/A'}
                                        invalid={isMissing}
                                        trailing={trailing}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            <AppTextField
                label="Motivo de corrección (opcional)"
                value={saveReason}
                onChange={e => onReasonChange(e.target.value)}
                placeholder="Ej: Corrección manual desde PDF"
            />

            <div className="flex justify-content-end gap-2" style={{ marginTop: '0.75rem' }}>
                <AppButton label="Cancelar" variant="secondary" size="sm" onClick={onCancel} disabled={saving} />
                <AppButton label="Guardar datos confirmados" variant="primary" size="sm"
                           onClick={onSave} loading={saving} />
            </div>

            {/* Overlay de confirmación de reversión */}
            <AnimatePresence>
            {confirmRevert && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    style={{
                        position: 'absolute', inset: 0,
                        backgroundColor: 'rgba(255,255,255,0.88)',
                        backdropFilter: 'blur(3px)',
                        borderRadius: '0.5rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 20,
                    }}
                >
                    <motion.div
                        initial={{ scale: 0.92, y: 6 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.92, y: 6 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        style={{
                            backgroundColor: '#fff', border: '1px solid #e5e7eb',
                            borderRadius: '0.625rem', padding: '1.25rem 1.5rem',
                            maxWidth: '280px', textAlign: 'center',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        }}
                    >
                        <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.35rem' }}>
                            ¿Revertir campo?
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.6rem' }}>
                            <strong>{confirmRevert.label}</strong> regresará al valor de la IA:
                        </div>
                        <div style={{
                            backgroundColor: '#f8fafc', border: '1px solid #e5e7eb',
                            borderRadius: '6px', padding: '0.45rem 0.75rem',
                            marginBottom: '1rem', fontSize: '0.875rem',
                            fontWeight: '600', color: '#0f172a',
                        }}>
                            {confirmRevert.aiValue !== null && confirmRevert.aiValue !== undefined
                                ? String(confirmRevert.aiValue)
                                : <span style={{ color: '#9ca3af', fontWeight: '400', fontStyle: 'italic' }}>Sin valor (vacío)</span>
                            }
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <AppButton label="Cancelar" variant="secondary" size="sm" onClick={() => setConfirmRevert(null)} />
                            <AppButton label="Revertir" variant="primary" size="sm"
                                       onClick={() => { onClearPath(confirmRevert.path); setConfirmRevert(null); }} />
                        </div>
                    </motion.div>
                </motion.div>
            )}
            </AnimatePresence>
        </div>
    );
};

// ─── Panel principal ─────────────────────────────────────────────────────────
const FinancialStatementsAnalysisPanel = ({ persona, toast, dirty, onDirtyReset }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const pollRef = useRef(null);

    // ─── Override edit state ──────────────────────────────────────────────────
    const [modalDoc, setModalDoc]                = useState(null);
    const [editValues, setEditValues]             = useState({});
    const [activeOverridePaths, setActiveOverridePaths] = useState([]);
    const [saveReason, setSaveReason]             = useState('');
    const [clearPathsQueue, setClearPathsQueue]   = useState([]);
    const [savingOverride, setSavingOverride]     = useState(false);
    const [openingEditId, setOpeningEditId]       = useState(null);
    const [loadingOverrides, setLoadingOverrides] = useState(false);

    const openEditForm = useCallback(async (doc) => {
        setOpeningEditId(doc.id);
        // Open modal immediately with loading state
        setModalDoc(doc);
        setLoadingOverrides(true);
        setClearPathsQueue([]);
        setSaveReason('');

        // Initialize form values from resolved_extracted
        const resolved = doc.resolved_extracted || doc.extracted || {};
        const initialValues = {};
        FIN_EDIT_SECTIONS.forEach(section => {
            section.fields.forEach(({ path }) => {
                const v = dotGet(resolved, path);
                if (v !== null && v !== undefined) initialValues[path] = v;
            });
        });
        setEditValues(initialValues);

        // Fetch active override paths
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${url}/document-analysis/items/${doc.id}/operational-overrides`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setActiveOverridePaths((res.data.overrides || []).map(o => o.field_path));
        } catch {
            setActiveOverridePaths([]);
        } finally {
            setOpeningEditId(null);
            setLoadingOverrides(false);
        }
    }, []);

    const handleClearPath = useCallback((path) => {
        setClearPathsQueue(q => q.includes(path) ? q : [...q, path]);
        setEditValues(v => { const n = { ...v }; delete n[path]; return n; });
        setActiveOverridePaths(p => p.filter(x => x !== path));
    }, []);

    // Fetch
    const fetchData = useCallback(async (silent = false) => {
        if (!persona?.id) return;
        if (!silent) setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${url}/document-analysis/edos-financieros/${persona.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setData(res.data);
        } catch (err) {
            if (err.response?.status === 404) {
                setData({ documents: [] });
            } else {
                toast?.current?.show({
                    severity: 'error', summary: 'Error',
                    detail: 'No se pudieron obtener los estados financieros.', life: 3000
                });
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [persona?.id]);

    const saveOverrides = useCallback(async (doc) => {
        setSavingOverride(true);
        try {
            const token = localStorage.getItem('token');
            const values = {};
            FIN_EDIT_SECTIONS.forEach(section => {
                section.fields.forEach(({ path, type }) => {
                    const v = editValues[path];
                    if (v !== undefined && v !== '' && v !== null) {
                        values[path] = type === 'number' ? Number(v) : v;
                    }
                });
            });
            await axios.post(
                `${url}/document-analysis/items/${doc.id}/operational-overrides`,
                {
                    category: 'ESTADOS FINANCIEROS',
                    save_reason: saveReason || 'Corrección manual desde panel financiero',
                    values,
                    clear_paths: clearPathsQueue,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast?.current?.show({ severity: 'success', summary: 'Guardado', detail: 'Datos confirmados guardados correctamente.', life: 3000 });
            setModalDoc(null);
            fetchData(true);
        } catch (err) {
            toast?.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'No se pudieron guardar los datos.', life: 4000 });
        } finally {
            setSavingOverride(false);
        }
    }, [editValues, saveReason, clearPathsQueue, fetchData]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Dirty flag
    useEffect(() => {
        if (dirty) { fetchData(); onDirtyReset?.(); }
    }, [dirty, fetchData, onDirtyReset]);

    // Polling
    const hasProcessing = data?.documents?.some(
        d => d.status === 'processing' || d.status === 'pending'
    );
    useEffect(() => {
        if (hasProcessing) {
            pollRef.current = setTimeout(() => fetchData(true), 10000);
        }
        return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    }, [data, hasProcessing, fetchData]);

    // ─── Derived data ────────────────────────────────────────────────────────
    const docs = useMemo(() =>
        (data?.documents || []).filter(d => d.review_status !== 'rejected'),
    [data]);

    const validDocs = useMemo(() =>
        docs.filter(isValidFinancialDoc),
    [docs]);

    const completedDocs = useMemo(() =>
        validDocs.filter(d => isDocCompleted(d.status)),
    [validDocs]);

    // Sorted completed docs for the switcher (most recent first)
    const switchableDocs = useMemo(() =>
        [...completedDocs].sort((a, b) => {
            if (a.period_end_date && b.period_end_date)
                return b.period_end_date.localeCompare(a.period_end_date);
            if (a.period_end_date) return -1;
            if (b.period_end_date) return 1;
            return b.id - a.id;
        }),
    [completedDocs]);

    // Document switcher state ('comparativa' | index number)
    const [selectedIdx, setSelectedIdx] = useState('comparativa');
    const previousSwitchableDocsCountRef = useRef(0);
    const isComparativa = selectedIdx === 'comparativa';

    // Keep comparativa as the default view when multiple statements are available.
    useEffect(() => {
        const previousCount = previousSwitchableDocsCountRef.current;

        if (switchableDocs.length === 1) {
            if (selectedIdx === 'comparativa' || (typeof selectedIdx === 'number' && selectedIdx >= switchableDocs.length)) {
                setSelectedIdx(0);
            }
        } else if (switchableDocs.length > 1) {
            if (
                (previousCount <= 1 && selectedIdx !== 'comparativa') ||
                (typeof selectedIdx === 'number' && selectedIdx >= switchableDocs.length)
            ) {
                setSelectedIdx('comparativa');
            }
        }

        previousSwitchableDocsCountRef.current = switchableDocs.length;
    }, [switchableDocs.length, selectedIdx]);

    const selectedDoc = isComparativa ? null : (switchableDocs[selectedIdx] || null);

    const workingCapital = useMemo(() => {
        if (!selectedDoc?.balance_general) return null;
        const ac = selectedDoc.balance_general.activo_circulante || 0;
        const pc = selectedDoc.balance_general.pasivo_circulante || 0;
        return ac - pc;
    }, [selectedDoc]);

    const workingCapitalRatio = useMemo(() => {
        if (!selectedDoc?.balance_general) return null;
        const ac = selectedDoc.balance_general.activo_circulante || 0;
        const pc = selectedDoc.balance_general.pasivo_circulante || 0;
        return pc > 0 ? ac / pc : null;
    }, [selectedDoc]);

    const sortedDocs = useMemo(() =>
        [...docs].sort((a, b) => {
            if (a.period_end_date && b.period_end_date)
                return b.period_end_date.localeCompare(a.period_end_date);
            if (a.period_end_date) return -1;
            if (b.period_end_date) return 1;
            return b.id - a.id;
        }),
    [docs]);

    // ─── Render ──────────────────────────────────────────────────────────────
    if (loading && !data) return <FinancialSkeleton />;

    if (!data || docs.length === 0) {
        return (
            <div className="flex flex-column align-items-center justify-content-center p-4 gap-3" style={{ minHeight: '10rem' }}>
                <i className="pi pi-folder-open text-3xl text-400" />
                <span className="text-sm text-500">No hay estados financieros para esta persona.</span>
                <AppButton label="Actualizar" icon="pi pi-refresh" variant="secondary" size="sm" onClick={() => fetchData()} loading={loading} />
            </div>
        );
    }

    const balance = selectedDoc?.balance_general;
    const resultados = selectedDoc?.estado_resultados;
    const ratios = selectedDoc?.ratios;

    return (
        <>
            <style>{`@keyframes finSkel{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
            <div className="flex flex-column gap-3">

                {/* Header */}
                <div className="flex align-items-center justify-content-between">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-percentage text-lg" style={{ color: TEAL }} />
                        <span className="text-sm font-bold" style={{ color: TEAL, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Analisis de Estados Financieros
                        </span>
                    </div>
                    <AppIconButton icon="pi pi-refresh" round onClick={() => fetchData()} loading={loading} title="Actualizar" />
                </div>

                {/* Document switcher */}
                {switchableDocs.length > 1 && (
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <button
                            onClick={() => setSelectedIdx('comparativa')}
                            style={{
                                background: isComparativa ? '#7B1FA215' : 'transparent',
                                border: isComparativa ? '1px solid #7B1FA2' : '1px solid #e5e7eb',
                                padding: '0.35rem 0.75rem',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: isComparativa ? '600' : '500',
                                color: isComparativa ? '#7B1FA2' : '#64748b',
                                borderRadius: '20px',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.35rem',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <i className="pi pi-chart-bar" style={{ fontSize: '0.7rem' }} />
                            Comparativa
                        </button>
                        {switchableDocs.map((doc, i) => {
                            const isActive = i === selectedIdx;
                            const label = doc.periodo || doc.empresa || doc.file_name || `Documento ${i + 1}`;
                            return (
                                <button
                                    key={doc.id}
                                    onClick={() => setSelectedIdx(i)}
                                    style={{
                                        background: isActive ? `${TEAL}15` : 'transparent',
                                        border: isActive ? `1px solid ${TEAL}` : '1px solid #e5e7eb',
                                        padding: '0.35rem 0.75rem',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                        fontWeight: isActive ? '600' : '500',
                                        color: isActive ? TEAL : '#64748b',
                                        borderRadius: '20px',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.35rem',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <i className="pi pi-file-edit" style={{ fontSize: '0.7rem' }} />
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Single doc indicator */}
                {switchableDocs.length === 1 && selectedDoc && (
                    <div className="flex align-items-center gap-2 text-xs text-500">
                        <i className="pi pi-file-edit" style={{ fontSize: '0.7rem' }} />
                        <span>{selectedDoc.empresa || selectedDoc.file_name}</span>
                        {selectedDoc.periodo && <span>| {selectedDoc.periodo}</span>}
                    </div>
                )}

                {/* ── Comparativa view ── */}
                {isComparativa && switchableDocs.length > 1 && (
                    <ComparativaView docs={switchableDocs} />
                )}

                {/* Operational override toolbar */}
                {selectedDoc && !isComparativa && (
                    <div className="flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="flex gap-2 flex-wrap">
                            {selectedDoc.has_manual_overrides && (
                                <span className="text-xs font-semibold px-2 py-1 border-round"
                                      style={{ background: `${TEAL}15`, color: TEAL }}>
                                    <i className="pi pi-check-circle mr-1" style={{ fontSize: '0.7rem' }} />
                                    Corregido manualmente ({selectedDoc.manual_override_count})
                                </span>
                            )}
                            {selectedDoc.operational_completeness?.missing_required_paths?.length > 0 && (
                                <div>
                                    <span className="text-xs font-semibold px-2 py-1 border-round"
                                          style={{ background: '#FFF8E1', color: '#E65100', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                                        <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.7rem' }} />
                                        Faltan {selectedDoc.operational_completeness.missing_required_paths.length} campos requeridos
                                    </span>
                                    <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                                        {selectedDoc.operational_completeness.missing_required_paths.map(p => (
                                            <span key={p} style={{ fontSize: '0.7rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                                <i className="pi pi-arrow-right" style={{ fontSize: '0.6rem' }} />
                                                {labelForFinPath(p)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {modalDoc?.id !== selectedDoc.id && (
                            <AppButton
                                label="Editar datos operativos"
                                icon="pi pi-pencil"
                                variant="info"
                                size="sm"
                                disabled={openingEditId === selectedDoc.id}
                                loading={openingEditId === selectedDoc.id}
                                onClick={() => openEditForm(selectedDoc)}
                            />
                        )}
                    </div>
                )}

                {/* ── Modal de edición ── */}
                <OperationalReviewModal
                    isOpen={!!modalDoc}
                    onClose={() => setModalDoc(null)}
                    title="Editar Datos Financieros"
                    subtitle={modalDoc ? `${modalDoc.empresa || modalDoc.file_name || 'Documento'} — ${modalDoc.periodo || ''}` : ''}
                    accentColor={TEAL}
                    documentUrl={modalDoc?.signed_url}
                    documentName={modalDoc?.file_name}
                    saving={savingOverride}
                    loading={loadingOverrides}
                    metaContent={modalDoc ? (
                        <div className="flex flex-column gap-2">
                            <div className="flex flex-wrap gap-2 align-items-center">
                                {modalDoc.empresa && (
                                    <span className="text-xs text-500">Empresa: <strong>{modalDoc.empresa}</strong></span>
                                )}
                                {modalDoc.periodo && (
                                    <span className="text-xs text-500">Periodo: <strong>{modalDoc.periodo}</strong></span>
                                )}
                                {modalDoc.has_manual_overrides && (
                                    <Tag value={`Corregido (${modalDoc.manual_override_count})`} rounded
                                         style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', background: `${TEAL}20`, color: TEAL }} />
                                )}
                            </div>
                            {modalDoc.operational_completeness?.missing_required_paths?.length > 0 && (
                                <div>
                                    <span className="text-xs font-semibold px-2 py-1 border-round"
                                          style={{ background: '#FFF8E1', color: '#E65100', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                                        <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.7rem' }} />
                                        Faltan {modalDoc.operational_completeness.missing_required_paths.length} campos requeridos
                                    </span>
                                    <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                                        {modalDoc.operational_completeness.missing_required_paths.map(p => (
                                            <span key={p} style={{ fontSize: '0.7rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                                <i className="pi pi-arrow-right" style={{ fontSize: '0.6rem' }} />
                                                {labelForFinPath(p)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}
                >
                    {modalDoc && (
                        <OperationalEditForm
                            doc={modalDoc}
                            activeOverridePaths={activeOverridePaths}
                            editValues={editValues}
                            saveReason={saveReason}
                            onFieldChange={(path, val) => setEditValues(v => ({ ...v, [path]: val }))}
                            onReasonChange={setSaveReason}
                            onClearPath={handleClearPath}
                            onSave={() => saveOverrides(modalDoc)}
                            onCancel={() => setModalDoc(null)}
                            saving={savingOverride}
                            missingRequiredPaths={modalDoc.operational_completeness?.missing_required_paths || []}
                        />
                    )}
                </OperationalReviewModal>


                {/* Executive summary cards */}
                {selectedDoc && !isComparativa && (
                    <div className="flex gap-3 flex-wrap">
                        <SummaryCard
                            icon="pi-shield"
                            title="Solvencia"
                            borderColor="#1565C0"
                            items={[
                                { label: 'Activo Total', value: fmtCurrency(balance?.total_activo) },
                                { label: 'Pasivo Total', value: fmtCurrency(balance?.total_pasivo) },
                                { label: 'Capital Contable', value: fmtCurrency(balance?.capital_contable), note: balance?.capital_contable_note },
                                { label: 'Razon Circulante', value: ratios?.razon_circulante != null ? ratios.razon_circulante.toFixed(2) : (workingCapitalRatio != null ? workingCapitalRatio.toFixed(2) : 'N/A'), note: ratios?.razon_circulante_note },
                            ]}
                        />
                        <SummaryCard
                            icon="pi-chart-line"
                            title="Rentabilidad"
                            borderColor="#2E7D32"
                            items={[
                                { label: 'Ventas Totales', value: fmtCurrency(resultados?.ventas_totales) },
                                { label: 'Utilidad Neta', value: fmtCurrency(resultados?.utilidad_neta) },
                                { label: 'Margen Neto', value: fmtPct(ratios?.margen_neto), note: ratios?.margen_neto_note },
                                { label: 'EBITDA', value: fmtCurrency(resultados?.ebitda), note: resultados?.ebitda_note },
                            ]}
                        />
                        <NarrativeCard
                            icon="pi-home"
                            title="Soporte al Arrendamiento"
                            borderColor="#E65100"
                            texts={[
                                { label: 'Impacto en Mensualidad', value: selectedDoc.impacto_mensualidad },
                                { label: 'Analisis Patrimonial', value: selectedDoc.analisis_patrimonial },
                            ]}
                        />
                    </div>
                )}

                {/* Charts */}
                {selectedDoc && !isComparativa && (
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ flex: '1 1 280px', minWidth: '260px' }}>
                            <div className="text-xs text-500 font-semibold mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Composicion Patrimonial
                            </div>
                            <ComposicionPatrimonialChart balance={balance} />
                        </div>
                        <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ flex: '1 1 280px', minWidth: '260px' }}>
                            <div className="text-xs text-500 font-semibold mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Estado de Resultados
                            </div>
                            <ResultadosChart resultados={resultados} />
                        </div>
                        <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ flex: '1 1 280px', minWidth: '260px' }}>
                            <div className="text-xs text-500 font-semibold mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Indicadores Clave
                            </div>
                            <IndicadoresChart ratios={ratios} workingCapitalRatio={workingCapitalRatio} />
                        </div>
                    </div>
                )}

                {/* Documents section */}
                <div>
                    <div className="flex align-items-center gap-2 mb-3 pb-2 border-bottom-1 surface-border">
                        <i className="pi pi-briefcase text-base" style={{ color: TEAL }} />
                        <span className="text-sm font-bold text-700" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Expedientes Financieros
                        </span>
                        <span className="ml-auto text-xs text-400">
                            {sortedDocs.length} {sortedDocs.length === 1 ? 'documento' : 'documentos'}
                        </span>
                    </div>

                    <div className="flex flex-column gap-3">
                        {sortedDocs.map(doc => (
                            <FinDocCard
                                key={doc.id}
                                doc={doc}
                                isLatest={selectedDoc && doc.id === selectedDoc.id}
                            />
                        ))}
                    </div>
                </div>

            </div>
        </>
    );
};

export default FinancialStatementsAnalysisPanel;
